import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { Calendar, Info, Package } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { getProductionUnitLabel } from "~/constants/units";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import {
  useUpdateBatchIngredients,
  useUpdateProductionBatch,
} from "~/hooks/production";
import { useToast } from "~/hooks/use-toast";
import type {
  BatchIngredientOverride,
  ProductionBatch,
  RecipeIngredient,
} from "~/types/production";

import { BatchIngredientsEditor } from "./BatchIngredientsEditor";

interface BatchEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batch: ProductionBatch;
  onSuccess: () => void;
}

export function BatchEditDialog({
  open,
  onOpenChange,
  batch,
  onSuccess,
}: BatchEditDialogProps) {
  const { toast } = useToast();
  const updateBatchMutation = useUpdateProductionBatch();
  const updateIngredientsMutation = useUpdateBatchIngredients();

  // Form state
  const [targetQuantity, setTargetQuantity] = useState(batch.target_quantity);
  const [startDate, setStartDate] = useState(batch.start_date);
  const [endDate, setEndDate] = useState(batch.end_date);
  const [expiryDate, setExpiryDate] = useState(batch.expiry_date);
  const [notes, setNotes] = useState(batch.notes || "");

  // Ingredient state
  const [ingredientOverrides, setIngredientOverrides] = useState<
    BatchIngredientOverride[]
  >([]);
  const [hasInsufficientStock, setHasInsufficientStock] = useState(false);

  // Convert existing batch ingredients to recipe ingredient format for editor
  const recipeIngredients: RecipeIngredient[] = (batch.ingredients || []).map(
    (ing) => ({
      id: ing.id,
      recipe_id: batch.recipe_id || "",
      raw_material_id: ing.raw_material_id,
      oil_type_id: ing.oil_type_id,
      quantity_per_box:
        ing.planned_quantity / (batch.target_quantity * (ing.is_override ? 1 : 1)), // Base quantity per box
      waste_factor: 1, // Waste factor already applied in planned_quantity
      notes: ing.notes,
      created_at: ing.created_at,
      raw_material: ing.raw_material,
      oil_type: ing.oil_type,
    })
  );

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setTargetQuantity(batch.target_quantity);
      setStartDate(batch.start_date);
      setEndDate(batch.end_date);
      setExpiryDate(batch.expiry_date);
      setNotes(batch.notes || "");
      setIngredientOverrides([]);
    }
  }, [batch, open]);

  // Validate dates
  const isValidDates = () => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const expiry = dayjs(expiryDate);
    return end.isAfter(start) || end.isSame(start, "day");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValidDates()) {
      toast({
        title: "Error de validación",
        description:
          "La fecha de fin debe ser igual o posterior a la fecha de inicio",
        variant: "destructive",
      });
      return;
    }

    try {
      // Update batch properties
      await updateBatchMutation.mutateAsync({
        id: batch.id,
        data: {
          target_quantity: targetQuantity,
          start_date: startDate,
          end_date: endDate,
          expiry_date: expiryDate,
          notes: notes || undefined,
        },
      });

      // Update ingredients if any overrides were made
      if (ingredientOverrides.length > 0) {
        await updateIngredientsMutation.mutateAsync({
          batchId: batch.id,
          overrides: ingredientOverrides,
          targetQuantity,
        });
      }

      toast({
        title: "Lote actualizado",
        description: "Los cambios han sido guardados exitosamente",
      });
      onSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Error al actualizar el lote",
        variant: "destructive",
      });
    }
  };

  const isPending =
    updateBatchMutation.isPending || updateIngredientsMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Editar Lote: {batch.batch_number}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Read-only batch info */}
          <Card className="bg-muted/50">
            <CardContent className="py-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Info className="h-4 w-4" />
                <span>Información no editable</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                <div>
                  <p className="text-muted-foreground">Producto</p>
                  <p className="font-medium">{batch.product?.short_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Receta</p>
                  <p className="font-medium">
                    {batch.recipe?.name || "Sin receta"}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Línea / Planta</p>
                  <p className="font-medium">
                    {batch.assembly_line || "-"} / {batch.plant || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Número de Lote</p>
                  <p className="font-mono font-medium">
                    {batch.lot_number || "-"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Editable fields */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="target_quantity">
                Cantidad Objetivo (
                {getProductionUnitLabel(batch.production_unit, 2)})
              </Label>
              <Input
                id="target_quantity"
                type="number"
                min="1"
                value={targetQuantity}
                onChange={(e) =>
                  setTargetQuantity(parseInt(e.target.value) || 0)
                }
                onFocus={(e) => e.target.select()}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notas</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Observaciones sobre el lote..."
                rows={1}
              />
            </div>
          </div>

          {/* Date fields */}
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="start_date" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Fecha de Inicio
              </Label>
              <Input
                id="start_date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="end_date" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Fecha de Fin
              </Label>
              <Input
                id="end_date"
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expiry_date" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Fecha de Vencimiento
              </Label>
              <Input
                id="expiry_date"
                type="date"
                value={expiryDate}
                min={endDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Ingredients Editor */}
          {batch.ingredients && batch.ingredients.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-base font-semibold">Ingredientes</Label>
                {hasInsufficientStock && (
                  <Badge
                    variant="outline"
                    className="border-yellow-300 bg-yellow-50 text-yellow-700"
                  >
                    Stock insuficiente
                  </Badge>
                )}
              </div>
              <BatchIngredientsEditor
                recipeIngredients={recipeIngredients}
                overrides={ingredientOverrides}
                onChange={setIngredientOverrides}
                targetQuantity={targetQuantity}
                onStockStatusChange={setHasInsufficientStock}
              />
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Guardando..." : "Guardar Cambios"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
